import React, { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.pm/dist/leaflet.pm.css";
import "leaflet.pm";
import "leaflet.heat";
import {
  Box,
  Container,
  Button,
  Stack,
  Popover,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Typography,
} from "@mui/material";
import Header from "../../components/GSO/Header";
import { useSidebar } from "../../components/SidebarContext";
import { getApiUrl, getApiHeaders } from "../../config/api";

const Heatmap = () => {
  const { isSidebarOpen } = useSidebar();
  const isMobile =
    /Mobi|Android/i.test(navigator.userAgent) || window.innerWidth <= 600;
  const mapRef = useRef(null);
  const polygonsLayerRef = useRef([]); // active Leaflet layers
  const heatLayerRef = useRef(null); // heatmap layer
  const itemMarkersLayerRef = useRef([]); // item location markers
  const mapFeaturesLayerRef = useRef([]); // OSM features (buildings, paths, etc.)
  const [savedPolygons, setSavedPolygons] = useState([]); // state is single source of truth
  const [reportedItems, setReportedItems] = useState([]); // reported items with locations
  const [showItems, setShowItems] = useState(false); // toggle item markers visibility
  const [editMode, setEditMode] = useState(false);
  const [selectedColor, setSelectedColor] = useState("#3357FF");
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);
  const [selectedPolygon, setSelectedPolygon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showHeatmap, setShowHeatmap] = useState(true); // toggle heatmap layer
  const [heatSettings, setHeatSettings] = useState({ radius: 25, blur: 15, minOpacity: 0.4 });

  // ========= Snackbar helper =========
  const showMessage = useCallback((message, isError = false) => {
    if (isError) {
      setError(message);
    } else {
      setSuccess(message);
    }
    setTimeout(() => {
      setError(null);
      setSuccess(null);
    }, 3000);
  }, []);

  // ========= Fetch polygons from backend =========
  const fetchPolygons = useCallback(() => {
    setLoading(true);
    fetch(getApiUrl("api/polygons"), {
      headers: getApiHeaders()
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const polys = data.map((p) => ({
            id: p.id,
            name: p.name,
            latlngs: JSON.parse(p.geojson).coordinates[0].map(([lng, lat]) => [
              lat,
              lng,
            ]),
            color: p.color,
            created_at: p.created_at,
          }));
          setSavedPolygons(polys);
        }
      })
      .catch(() => showMessage("Failed to fetch polygons", true))
      .finally(() => setLoading(false));
  }, [showMessage]);

  // ========= Fetch reported items from backend =========
  const fetchReportedItems = useCallback(() => {
    fetch(getApiUrl("found-item/list"), {
      headers: getApiHeaders()
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.items && Array.isArray(data.items)) {
          setReportedItems(data.items);
        }
      })
      .catch(() => showMessage("Failed to fetch reported items", true));
  }, [showMessage]);

  // ========= Refresh and reset map view =========
  const handleRefresh = useCallback(() => {
    fetchReportedItems();
    if (mapRef.current && mapRef.current._loaded) {
      mapRef.current.setView([10.713335, 122.562388], 20);
    }
  }, [fetchReportedItems]);

  // ========= Save polygons to backend =========
  const handleSave = useCallback(() => {
    setLoading(true);
    fetch(getApiUrl("api/polygons"), {
      method: "POST",
      headers: getApiHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(savedPolygons),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Save failed");
        return res.json();
      })
      .then(() => {
        showMessage("Changes saved successfully");
      })
      .catch(() => showMessage("Failed to save changes", true))
      .finally(() => setLoading(false));
  }, [savedPolygons, showMessage]);

  // ========= Handle new polygon creation =========
  const handleCreate = useCallback(
    (e) => {
      if (!e.layer) return;
      const coords = e.layer
        .getLatLngs()[0]
        .map((c) => [c.lat, c.lng]);

      const newPoly = {
        id: Date.now(),
        name: "Unnamed Area",
        latlngs: coords,
        color: selectedColor,
        created_at: new Date().toISOString(),
      };

      setSavedPolygons((prev) => [...prev, newPoly]);
      e.layer.remove(); // remove temporary drawn layer (we’ll re-render via state)
    },
    [selectedColor]
  );

  // ========= Handle polygon removal =========
  const handleRemove = useCallback(
    (e) => {
      if (!e.layer) return;
      const coords = e.layer
        .getLatLngs()[0]
        .map((c) => `${c.lat},${c.lng}`)
        .join("|");

      setSavedPolygons((prev) =>
        prev.filter(
          (poly) =>
            poly.latlngs.map(([lat, lng]) => `${lat},${lng}`).join("|") !==
            coords
        )
      );
    },
    []
  );

  // ========= Handle polygon edit (move/resize) =========
  const handleEdit = useCallback((e) => {
    if (!e.layer) return;
    // For each edited layer, update its coordinates in savedPolygons by id
    const editedLayers = e.layers ? e.layers.getLayers() : [e.layer];
    setSavedPolygons((prev) => {
      let updated = [...prev];
      editedLayers.forEach((layer) => {
        const editedCoords = layer.getLatLngs()[0].map((c) => [c.lat, c.lng]);
        const editedId = layer._ufindId;
        updated = updated.map((poly) =>
          poly.id === editedId ? { ...poly, latlngs: editedCoords } : poly
        );
      });
      return updated;
    });
  }, []);

  // ========= Handle polygon click (for color edit) =========
  const handlePolygonClick = useCallback(
    (layer, poly) => {
      if (!editMode) return;

      if (selectedPolygon && selectedPolygon.layer) {
        selectedPolygon.layer.setStyle({
          weight: 2,
          fillOpacity: 0.3,
          opacity: 1,
        });
      }

      if (selectedPolygon?.id === poly.id) {
        setSelectedPolygon(null);
        layer.setStyle({
          weight: 2,
          fillOpacity: 0.3,
          opacity: 1,
        });
      } else {
        setSelectedPolygon({ id: poly.id, layer });
        setSelectedColor(poly.color);
        layer.setStyle({
          color: poly.color,
          fillColor: poly.color,
          weight: 4,
          fillOpacity: 0.5,
          opacity: 1,
        });
        setColorPickerAnchor(document.getElementById("colorButton"));
      }
    },
    [editMode, selectedPolygon]
  );

  // ========= Handle color preview =========
  const handleColorPreview = useCallback(
    (e) => {
      const newColor = e.target.value;
      setSelectedColor(newColor);
      if (selectedPolygon?.layer) {
        selectedPolygon.layer.setStyle({
          color: newColor,
          fillColor: newColor,
          weight: 4,
          fillOpacity: 0.5,
        });
      }
    },
    [selectedPolygon]
  );

  // ========= Save color change =========
  const handleColorSave = useCallback(() => {
    if (!selectedPolygon) return;

    setSavedPolygons((prev) =>
      prev.map((poly) =>
        poly.id === selectedPolygon.id ? { ...poly, color: selectedColor } : poly
      )
    );

    setColorPickerAnchor(null);
    showMessage("Color updated (not saved to DB until Save Polygons)");
  }, [selectedPolygon, selectedColor, showMessage]);

  // ========= Init map =========
  useEffect(() => {
    if (!mapRef.current) {
      const map = L.map("map", {
        center: [10.713335, 122.562388],
        zoom: 20,
        zoomControl: true,
        scrollWheelZoom: true,
      });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
        className: "grayscale-tiles",
      }).addTo(map);
      mapRef.current = map;
      
      // Fetch and draw OSM features
      fetchMapFeatures(map);
    }
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);
  
  // ========= Fetch map features from OpenStreetMap =========
  const fetchMapFeatures = async (map) => {
    try {
      const centerLat = 10.713335;
      const centerLng = 122.562388;
      const radius = 200;
      
      const query = `
        [out:json][timeout:25];
        (
          way["building"](around:${radius},${centerLat},${centerLng});
          way["highway"](around:${radius},${centerLat},${centerLng});
          way["amenity"](around:${radius},${centerLat},${centerLng});
          way["leisure"](around:${radius},${centerLat},${centerLng});
          way["landuse"](around:${radius},${centerLat},${centerLng});
          way["natural"](around:${radius},${centerLat},${centerLng});
          way["area"](around:${radius},${centerLat},${centerLng});
          relation["building"](around:${radius},${centerLat},${centerLng});
        );
        out body;
        >;
        out skel qt;
      `;
      
      const response = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        body: query,
      });
      
      if (!response.ok) return;
      
      const data = await response.json();
      const nodes = {};
      data.elements.forEach(element => {
        if (element.type === 'node') {
          nodes[element.id] = [element.lat, element.lon];
        }
      });
      
      data.elements.forEach(element => {
        if (element.type === 'way' && element.nodes) {
          const coordinates = element.nodes
            .map(nodeId => nodes[nodeId])
            .filter(coord => coord);
          
          if (coordinates.length > 2) {
            const isPolygon = coordinates.length > 2 && 
                            element.nodes[0] === element.nodes[element.nodes.length - 1];
            
            let layer;
            if (isPolygon) {
              layer = L.polygon(coordinates, {
                color: '#999',
                fillColor: '#ccc',
                fillOpacity: 0.4,
                weight: 1,
              }).addTo(map);
            } else {
              layer = L.polyline(coordinates, {
                color: '#999',
                weight: 3,
                opacity: 0.6,
              }).addTo(map);
            }
            
            mapFeaturesLayerRef.current.push({
              layer: layer,
              name: element.tags?.name || element.tags?.highway || element.tags?.amenity || 'Unknown',
              coordinates: coordinates,
              isPolygon: isPolygon,
            });
          }
        }
      });
    } catch (error) {
      console.error('Failed to fetch map features:', error);
    }
  };

  // ========= Draw polygons whenever savedPolygons changes =========
  useEffect(() => {
    if (!mapRef.current) return;

    polygonsLayerRef.current.forEach((layer) => {
      mapRef.current.removeLayer(layer);
    });
    polygonsLayerRef.current = [];

    savedPolygons.forEach((poly) => {
      const layer = L.polygon(poly.latlngs, {
        color: poly.color,
        fillColor: poly.color,
        fillOpacity: 0.3,
        weight: 2,
      }).addTo(mapRef.current);

      // Attach the polygon's id to the Leaflet layer for later reference
      layer._ufindId = poly.id;

      layer.on("click", () => handlePolygonClick(layer, poly));
      polygonsLayerRef.current.push(layer);
    });

    if (mapRef.current.pm) {
      mapRef.current.pm.removeControls();
    }
    if (editMode) {
      mapRef.current.pm.addControls({
        position: "topleft",
        drawPolygon: true,
        editMode: true,
        dragMode: true,
        removalMode: true,
        drawCircle: false,
        drawMarker: false,
        drawPolyline: false,
        drawRectangle: false,
        drawCircleMarker: false,
        drawText: false,
        cutPolygon: false,
      });
      mapRef.current.on("pm:create", handleCreate);
      mapRef.current.on("pm:remove", handleRemove);
      mapRef.current.on("pm:edit", handleEdit);
      mapRef.current.on("pm:dragend", handleEdit);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.off("pm:create", handleCreate);
        mapRef.current.off("pm:remove", handleRemove);
        mapRef.current.off("pm:edit", handleEdit);
        mapRef.current.off("pm:dragend", handleEdit);
      }
    };
  }, [savedPolygons, editMode, handleCreate, handleRemove, handleEdit, handlePolygonClick]);

  // ========= Load polygons at mount =========
  useEffect(() => {
    fetchPolygons();
      fetchReportedItems();
    }, [fetchPolygons, fetchReportedItems]);

  // ========= Auto-refresh items periodically =========
  useEffect(() => {
    const id = setInterval(() => {
      fetchReportedItems();
    }, 60000); // every 60s
    return () => clearInterval(id);
  }, [fetchReportedItems]);

    // ========= Render item markers whenever reportedItems or showItems changes =========
    useEffect(() => {
      if (!mapRef.current) return;

      // Remove existing item markers
      itemMarkersLayerRef.current.forEach((marker) => {
        mapRef.current.removeLayer(marker);
      });
      itemMarkersLayerRef.current = [];

      if (!showItems) return;

      // Create custom icon for items
      const itemIcon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="
          background-color: #FF6B6B;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 2px 6px rgba(0,0,0,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          color: white;
          font-weight: bold;
        ">📍</div>`,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
        popupAnchor: [0, -12],
      });

      // Add markers for each reported item
      reportedItems.forEach((item) => {
        if (item.latitude && item.longitude) {
          const marker = L.marker([item.latitude, item.longitude], {
            icon: itemIcon,
          }).addTo(mapRef.current);

          // Create popup content
          const popupContent = `
            <div style="padding: 10px; max-width: 250px;">
              <h3 style="margin: 0 0 8px 0; color: #516d4f; font-size: 16px; font-weight: 600;">
                ${item.item_name || 'Unknown Item'}
              </h3>
              <div style="margin: 4px 0; color: #555; font-size: 14px;">
                <strong>Type:</strong> ${item.item_type || 'N/A'}
              </div>
              ${item.color ? `<div style="margin: 4px 0; color: #555; font-size: 14px;">
                <strong>Color:</strong> ${item.color}
              </div>` : ''}
              <div style="margin: 4px 0; color: #555; font-size: 14px;">
                <strong>Location:</strong> ${item.location_found || 'N/A'}
              </div>
              <div style="margin: 4px 0; color: #555; font-size: 14px;">
                <strong>Date:</strong> ${item.date_found || 'N/A'}
              </div>
              ${item.founder_name ? `<div style="margin: 4px 0; color: #555; font-size: 14px;">
                <strong>Found by:</strong> ${item.founder_name}
              </div>` : ''}
              ${item.item_condition ? `<div style="margin: 4px 0; color: #555; font-size: 14px;">
                <strong>Condition:</strong> ${item.item_condition}
              </div>` : ''}
            </div>
          `;

          marker.bindPopup(popupContent);
          itemMarkersLayerRef.current.push(marker);
        }
      });
    }, [reportedItems, showItems]);

    // ========= Render heatmap overlay =========
    useEffect(() => {
      if (!mapRef.current) return;

      // Remove existing heat layer
      if (heatLayerRef.current) {
        mapRef.current.removeLayer(heatLayerRef.current);
        heatLayerRef.current = null;
      }

      if (!showHeatmap) return;

      // Build heat data: [lat, lng, intensity]
      const points = reportedItems
        .filter((it) => it.latitude && it.longitude)
        .map((it) => [it.latitude, it.longitude, 0.6]);

      if (points.length === 0) return;

      heatLayerRef.current = L.heatLayer(points, {
        radius: heatSettings.radius,
        blur: heatSettings.blur,
        minOpacity: heatSettings.minOpacity,
        maxZoom: 20,
        gradient: {
          0.0: "#ffcccc",
          0.3: "#ff9999",
          0.5: "#ff6666",
          0.7: "#ff3333",
          1.0: "#cc0000",
        },
      }).addTo(mapRef.current);
    }, [reportedItems, showHeatmap, heatSettings]);
    
  // ========= Color map features based on item density =========
  useEffect(() => {
    if (!mapRef.current || mapFeaturesLayerRef.current.length === 0) return;
    if (reportedItems.length === 0) return;
    
    // Group items by location
    const locationCounts = {};
    reportedItems.forEach((item) => {
      const location = item.location_found || "Unknown";
      if (!locationCounts[location]) {
        locationCounts[location] = {
          count: 0,
          items: [],
        };
      }
      locationCounts[location].count++;
      locationCounts[location].items.push(item);
    });
    
    // Get coordinates for each location
    const locationStats = Object.entries(locationCounts).map(([location, data]) => {
      const validItems = data.items.filter(item => {
        const lat = parseFloat(item.latitude);
        const lng = parseFloat(item.longitude);
        return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
      });
      
      let avgLat = null;
      let avgLng = null;
      
      if (validItems.length > 0) {
        avgLat = validItems.reduce((sum, item) => sum + parseFloat(item.latitude), 0) / validItems.length;
        avgLng = validItems.reduce((sum, item) => sum + parseFloat(item.longitude), 0) / validItems.length;
      } else {
        const coordMatch = location.match(/^(-?\d+\.\d+),\s*(-?\d+\.\d+)$/);
        if (coordMatch) {
          avgLat = parseFloat(coordMatch[1]);
          avgLng = parseFloat(coordMatch[2]);
        }
      }
      
      return {
        location,
        totalItems: data.count,
        latitude: avgLat,
        longitude: avgLng,
      };
    }).filter(stat => stat.latitude && stat.longitude)
      .sort((a, b) => b.totalItems - a.totalItems);
    
    if (locationStats.length === 0) return;
    
    const maxItems = locationStats[0].totalItems;
    
    // Helper function for point-in-polygon
    const isPointInPolygon = (point, polygon) => {
      let inside = false;
      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i][1], yi = polygon[i][0];
        const xj = polygon[j][1], yj = polygon[j][0];
        const intersect = ((yi > point.lat) !== (yj > point.lat))
            && (point.lng < (xj - xi) * (point.lat - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
      }
      return inside;
    };
    
    // Get color based on intensity
    const getIntensityColor = (intensity) => {
      if (intensity >= 0.8) return "#516d4f";
      if (intensity >= 0.6) return "#6b8a68";
      if (intensity >= 0.4) return "#8bb174";
      if (intensity >= 0.2) return "#a5c38e";
      return "#d0e7ba";
    };
    
    // Color features (defensive checks to avoid Leaflet race conditions)
    mapFeaturesLayerRef.current.forEach((feature) => {
      let featureItemCount = 0;
      
      locationStats.forEach(stat => {
        const point = { lat: stat.latitude, lng: stat.longitude };
        
        if (feature.isPolygon) {
          const isInside = isPointInPolygon(point, feature.coordinates);
          if (isInside) {
            featureItemCount += stat.totalItems;
          }
        }
        
        const leafletPoint = L.latLng(stat.latitude, stat.longitude);
        // Guard against layers not yet fully attached (prevents _leaflet_pos errors)
        if (!feature.layer._map || typeof feature.layer.getBounds !== 'function') {
          return; // skip until layer is ready
        }
        let distance = Infinity;
        try {
          const featureBounds = feature.layer.getBounds();
          const featureCenter = featureBounds.getCenter();
          distance = leafletPoint.distanceTo(featureCenter);
        } catch (e) {
          // If bounds computation fails, skip proximity logic for this feature
          return;
        }
        
        if (distance < 25 && featureItemCount === 0) {
          featureItemCount += stat.totalItems;
        }
      });
      
      if (featureItemCount > 0) {
        const intensity = featureItemCount / maxItems;
        const color = getIntensityColor(intensity);
        
        if (feature.isPolygon) {
          feature.layer.setStyle({
            fillColor: color,
            fillOpacity: 0.7,
            color: color,
            weight: 2,
          });
        } else {
          feature.layer.setStyle({
            color: color,
            weight: 5,
            opacity: 0.8,
          });
        }
      } else {
        // Reset to default gray
        if (feature.isPolygon) {
          feature.layer.setStyle({
            color: '#999',
            fillColor: '#ccc',
            fillOpacity: 0.4,
            weight: 1,
          });
        } else {
          feature.layer.setStyle({
            color: '#999',
            weight: 3,
            opacity: 0.6,
          });
        }
      }
    });
  }, [reportedItems]);

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#FBFFE9",
        padding: 0,
        margin: 0,
        overflow: "hidden",
        position: "relative",
  transition: "margin-left 0.3s cubic-bezier(0.4,0,0.2,1), max-width 0.3s cubic-bezier(0.4,0,0.2,1)",
        marginLeft: isSidebarOpen ? (isMobile ? "200px" : "300px") : 0,
        maxWidth: isSidebarOpen
          ? isMobile
            ? "calc(100vw - 200px)"
            : "calc(100vw - 300px)"
          : "100vw",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
  {/* Header always on top */}
  <Header title="Heatmap" />

      {/* Main container with Paper background */}
      <Container maxWidth="xl" sx={{ mt: 3, px: 6 }}>
        <Paper elevation={4} sx={{ bgcolor: "#E1ECC8", p: 4, borderRadius: "30px" }}>
          <Box
            sx={{
              height: "686px",
              width: "100%",
              maxWidth: "100%",
              mx: "auto",
              borderRadius: "20px",
              overflow: "hidden",
              background: "#FBFFE9",
              position: "relative",
            }}
          >
            {/* Control buttons overlay */}
            <Stack
              direction="row"
              spacing={1}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 1000,
                p: 1.5,
                borderRadius: "12px",
              }}
            >
              <Button
                variant={showHeatmap ? "contained" : "outlined"}
                color="primary"
                size="small"
                onClick={() => setShowHeatmap((prev) => !prev)}
                sx={{ 
                  minWidth: "120px",
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                {showHeatmap ? "Hide Heatmap" : "Show Heatmap"}
              </Button>
              <Button
                variant={showItems ? "contained" : "outlined"}
                color="primary"
                size="small"
                onClick={() => setShowItems((prev) => !prev)}
                sx={{ 
                  minWidth: "120px",
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                {showItems ? "Hide Items" : "Show Items"}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleRefresh}
                sx={{ 
                  minWidth: "100px",
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                Refresh
              </Button>
            </Stack>
            
            <Box 
              id="map" 
              sx={{ 
                height: "100%", 
                width: "100%",
                "& .grayscale-tiles": {
                  filter: "grayscale(100%) contrast(1.1)",
                  WebkitFilter: "grayscale(100%) contrast(1.1)",
                },
              }}
            />
          </Box>
          
          {/* Intensity legend outside map (match HotspotLocations) */}
          {!loading && (
            <Box sx={{ mt: 2, width: "100%", px: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Typography 
                  variant="caption" 
                  color="#516d4f" 
                  fontWeight="medium"
                  sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" } }}
                >
                  Intensity:
                </Typography>
                <Box sx={{ display: "flex", height: "14px", flex: 1, borderRadius: "4px", overflow: "hidden", border: "1px solid #516d4f" }}>
                  <Box sx={{ flex: 1, bgcolor: "#d0e7ba" }} />
                  <Box sx={{ flex: 1, bgcolor: "#a5c38e" }} />
                  <Box sx={{ flex: 1, bgcolor: "#8bb174" }} />
                  <Box sx={{ flex: 1, bgcolor: "#6b8a68" }} />
                  <Box sx={{ flex: 1, bgcolor: "#516d4f" }} />
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", px: 0.5 }}>
                <Typography 
                  variant="caption" 
                  color="#7a9677"
                  sx={{ fontSize: { xs: "0.6rem", sm: "0.65rem", md: "0.7rem" } }}
                >
                  Low
                </Typography>
                <Typography 
                  variant="caption" 
                  color="#7a9677"
                  sx={{ fontSize: { xs: "0.6rem", sm: "0.65rem", md: "0.7rem" } }}
                >
                  High
                </Typography>
              </Box>
            </Box>
          )}
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 1000,
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </Paper>
      </Container>
      <Popover
        open={Boolean(colorPickerAnchor)}
        anchorEl={colorPickerAnchor}
        onClose={() => setColorPickerAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, bgcolor: "white" }}>
          <Stack spacing={2}>
            <TextField
              type="color"
              value={selectedColor}
              onChange={handleColorPreview}
              sx={{ width: 200 }}
              inputProps={{
                style: { cursor: "pointer", height: 50 },
              }}
            />
            <Button
              variant="contained"
              onClick={handleColorSave}
              fullWidth
              sx={{ mt: 1 }}
            >
              Done
            </Button>
          </Stack>
        </Box>
      </Popover>
      <Snackbar
        open={Boolean(error || success)}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={error ? "error" : "success"}>{error || success}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Heatmap;